import { Button, Form, InputNumber, Modal, Select, message } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { partsRestCrudThunksForSupplier } from '../../thunks/parts_thunks';
import {
	getCurrency,
	getEntityById,
	getObjectValues,
	getPurchaseRequestId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { partCatalogsRestCrudThunksForSupplier } from '../../thunks/part_catalogs_thunks';
import ModalFormFooter from '../common/ModalFormFooter';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import { unitsOfMeasureRestCrudThunksForSupplier } from '../../thunks/units_of_measure_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { v4 as uuiv4 } from 'uuid';
import PartEquipmentCostFormField from '../common/PartEquipmentCostFormField';
import { PERMISSION_NAMES, isSupplierAllowedToAccess } from '../../utils/AuthUtils';
import PartWithStockedDropdownDisplay from './PartWithStockedDropdownDisplay';
import NewPartFormFields from '../supplier_work_orders_detail_charges_page/NewPartFormFields';

const FORM_ID = 'add-part-form';
const PART_VENDORS_TC_NAME = 'PART_VENDORS_AUTOCOMPLETE';
const PARTS_TC_NAME = 'stockLocationAssociatedParts';

const AddPartInPRFrom: FC<any> = ({
	onCancel,
	parts,
	fetchPart,
	fetchParts,
	fetchMultipleParts,
	fetchPartsCatalog,
	fetchVendors,
	fetchMultipleVendors,
	createVendor,
	createPartsPerCatalog,
	currentUser,
	fetchUnitsOfMeasure,
	unitsOfMeasure,
	createPart,
	updatePart,
	onSuccess,
	allParts,
	partData,
	vendors,
	purchaseRequests,
	loading,
	companyConfig,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [addingPart, setAddingPart] = useState(false);
	const [newPart, setNewPart] = useState(null);
	// Holds the part once created, so a retry after a failed catalog request doesn't create it twice.
	const createdNewPartRef = useRef(null);
	const submittingRef = useRef(false);
	const userCurrencyId = nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD');

	const { setFieldsValue } = form;

	const watchedPartId = Form.useWatch('partId', form) || nullSafeGet('partId', partData);
	// A new part has no id yet; don't fall back to the edited line's part and its prices.
	const partId = newPart ? undefined : watchedPartId;
	const vendorId = Form.useWatch('vendorId', form) || nullSafeGet('vendorId', partData);
	const unitCost = Form.useWatch('unitCost', form) || nullSafeGet('unitCost', partData);
	const quantity = Form.useWatch('quantity', form) || nullSafeGet('quantity', partData);

	const getVendorCurrencyId = useCallback(
		(id) => nullSafeGetOrElse('currencyId', getEntityById(id, vendors), userCurrencyId),
		[userCurrencyId, vendors]
	);

	const currencyId = useMemo(() => getVendorCurrencyId(vendorId), [getVendorCurrencyId, vendorId]);

	const partRecord = useMemo(() => {
		if (newPart) return undefined;
		const partsRecords = getRecordsForTargetCollection(parts, PARTS_TC_NAME);
		return partsRecords.find((_) => _.id === partId) || nullSafeGet('part', partData);
	}, [newPart, partData, partId, parts]);

	useEffect(() => {
		nullSafeGet('defaultVendorId', partRecord) &&
			!vendorId &&
			setFieldsValue({ vendorId: nullSafeGet('defaultVendorId', partRecord) });
	}, [partRecord, setFieldsValue, vendorId]);

	useEffect(() => {
		fetchUnitsOfMeasure();
	}, [fetchUnitsOfMeasure]);

	const supplierCompanyId = useMemo(
		() => nullSafeGet('facility.supplierCompanyId', currentUser),
		[currentUser]
	);
	const supplierFacilityId = useMemo(() => nullSafeGet('facility.id', currentUser), [currentUser]);

	const defaultUnitOfMeasureId = useMemo(() => {
		const unitsOfMeasureRecords =
			unitsOfMeasure && unitsOfMeasure.records ? getObjectValues(unitsOfMeasure.records) : [];
		const eachUnit = unitsOfMeasureRecords.find((_) => _.displayNameSingular === 'each');
		return nullSafeGetOrElse('id', eachUnit, 1);
	}, [unitsOfMeasure]);

	const getPartCatalogFromVendorId = useCallback(
		(vendorId, partId, catalogCurrencyId, catalogUnitCost?) => {
			return new Promise((resolve, reject) => {
				if (vendorId) {
					fetchPartsCatalog({
						partId,
						partEquipmentVendorId: vendorId,
					})
						.then((res) => {
							if (res && res.length > 0) {
								resolve(res[0]);
							} else {
								createPartsPerCatalog({
									partEquipmentVendorId: vendorId,
									currencyId: catalogCurrencyId,
									unitCost: catalogUnitCost ? `${catalogUnitCost}` : undefined,
									partId,
									supplierFacilityId,
									supplierCompanyId,
								})
									.then((ppc) => resolve(ppc))
									.catch((err) => reject(err));
							}
						})
						.catch((err) =>
							createPartsPerCatalog({
								partEquipmentVendorId: vendorId,
								currencyId: catalogCurrencyId,
								unitCost: catalogUnitCost,
								partId,
								supplierFacilityId,
								supplierCompanyId,
							})
								.then((ppc) => resolve(ppc))
								.catch((err) => reject(err))
						);
				} else resolve(null);
			});
		},
		[fetchPartsCatalog, createPartsPerCatalog, supplierCompanyId, supplierFacilityId]
	);

	const getPartRecord = useCallback((): any => {
		return new Promise((resolve, reject) => {
			partRecord
				? resolve(partRecord)
				: fetchPart(partId)
						.then((res) => resolve(res))
						.catch((err) => reject(err));
		});
	}, [partRecord, fetchPart, partId]);

	const createNewPartWithCatalogs = useCallback(
		(values): Promise<any[]> => {
			const partFields = {
				name: values.name,
				partNumber: values.partNumber,
				defaultVendorId: values.defaultVendorId,
			};
			// On retry, save the edited fields onto the part already created.
			const savePart = createdNewPartRef.current
				? updatePart({ ...createdNewPartRef.current, ...partFields })
				: createPart({
						...partFields,
						uomId: defaultUnitOfMeasureId,
						supplierCompanyId,
						supplierFacilityId,
						images: [],
				  });
			return savePart.then((part) => {
				createdNewPartRef.current = part;
				// Sequential, not Promise.all: concurrent catalog reads cancel each other (TakeLatest).
				return getPartCatalogFromVendorId(
					values.vendorId,
					part.id,
					getVendorCurrencyId(values.vendorId),
					unitCost
				)
					.then((partCatalog) =>
						values.defaultVendorId && values.defaultVendorId !== values.vendorId
							? getPartCatalogFromVendorId(
									values.defaultVendorId,
									part.id,
									getVendorCurrencyId(values.defaultVendorId)
							  ).then(() => partCatalog)
							: partCatalog
					)
					.then((partCatalog) => [partCatalog, part]);
			});
		},
		[
			createPart,
			updatePart,
			unitCost,
			defaultUnitOfMeasureId,
			getPartCatalogFromVendorId,
			getVendorCurrencyId,
			supplierCompanyId,
			supplierFacilityId,
		]
	);

	const allPartUniqueIds = useMemo(() => allParts.map((_) => _.id), [allParts]);

	const isEditingPart = useMemo(
		() => partData && allPartUniqueIds.includes(partData.id),
		[partData, allPartUniqueIds]
	);

	const allPartIds = useMemo(() => allParts.map((_) => nullSafeGet('part.id', _)), [allParts]);

	const handleSubmit = useCallback(
		(values) => {
			// The submit button re-renders as loading a tick late; a second submit would create a second part.
			if (submittingRef.current) return;
			submittingRef.current = true;
			setAddingPart(true);
			const partAndCatalog: Promise<any[]> = newPart
				? createNewPartWithCatalogs(values)
				: Promise.all([
						getPartCatalogFromVendorId(values.vendorId, partId, 'USD', unitCost),
						getPartRecord(),
				  ]);
			partAndCatalog
				.then(([partCatalog, part]) => {
					// The new-part fields describe the part, not the line item.
					const { name, partNumber, defaultVendorId, ...lineItemValues } = values;
					const newRecord = {
						...lineItemValues,
						...(newPart && { partId: part.id }),
						part,
						...(partCatalog && { partCatalog }),
						...(partCatalog && { partCatalogId: partCatalog.id }),
					};

					const newParts = isEditingPart
						? allParts.map((_) => (_.id === partData.id ? newRecord : _))
						: [...allParts, ...[{ ...newRecord, id: uuiv4() }]];

					onSuccess && onSuccess(newParts);
				})
				.catch((err) => message.error(err))
				.finally(() => {
					submittingRef.current = false;
					setAddingPart(false);
				});
		},
		[
			newPart,
			createNewPartWithCatalogs,
			getPartCatalogFromVendorId,
			getPartRecord,
			partId,
			unitCost,
			isEditingPart,
			allParts,
			onSuccess,
			partData,
		]
	);

	const startNewPart = (name) => {
		createdNewPartRef.current = null;
		// Antd keeps values of unmounted fields, so reset them to the typed name every time.
		setFieldsValue({ name, partNumber: name, defaultVendorId: undefined });
		setNewPart({ name });
		return Promise.resolve(null);
	};

	const selectExistingPart = () => {
		createdNewPartRef.current = null;
		setNewPart(null);
	};

	// Same as an existing part: its default vendor fills an empty Vendor.
	const prefillVendorFromDefaultVendor = (changedValues, allValues) =>
		changedValues.defaultVendorId &&
		!allValues.vendorId &&
		setFieldsValue({ vendorId: changedValues.defaultVendorId });

	const prSelectionExist = useMemo(() => !!nullSafeGet('0', purchaseRequests), [purchaseRequests]);

	const canCreatePartOrEquipment = useMemo(
		() =>
			isSupplierAllowedToAccess(
				PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
				companyConfig,
				currentUser.roles
			),
		[companyConfig, currentUser.roles]
	);

	const canCreateVendors = isSupplierAllowedToAccess(
		PERMISSION_NAMES.MODIFY_VENDORS,
		companyConfig,
		currentUser.roles
	);

	return (
		<Modal
			visible={true}
			title={'Add Part to Purchase Request'}
			onCancel={onCancel}
			bodyStyle={{ minHeight: '480px' }}
			footer={null}
			closable={true}
		>
			<Form
				layout="vertical"
				onFinish={handleSubmit}
				onValuesChange={prefillVendorFromDefaultVendor}
				initialValues={partData || {}}
				id={FORM_ID}
				form={form}
			>
				{prSelectionExist ? (
					<Form.Item
						name="supplierPurchaseRequestId"
						label="Purchase Request"
						rules={[
							{
								required: true,
								message: 'Please select a purchase request.',
							},
						]}
					>
						<Select disabled={purchaseRequests.length === 1}>
							{purchaseRequests.map((pr) => (
								<Select.Option value={pr.id} key={pr.id}>
									<div>{`${getPurchaseRequestId(pr)} - ${pr.name}`}</div>
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				) : null}
				{newPart ? (
					<>
						<NewPartFormFields part={newPart} />
						<Button
							type="link"
							className="-mt-4 mb-2 px-0"
							disabled={addingPart}
							onClick={selectExistingPart}
						>
							Select an existing part instead
						</Button>
					</>
				) : (
					<Form.Item
						name="partId"
						label="Part"
						rules={[
							{
								required: true,
								message: 'Please select a part.',
							},
						]}
					>
						<OWAsyncSelect
							stateSlice={parts}
							targetCollectionName={PARTS_TC_NAME}
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleParts(ids, targetCollectionName);
							}}
							fetchData={(
								search,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) => {
								fetchParts(
									{ search },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
							}}
							createNewEntity={canCreatePartOrEquipment ? startNewPart : undefined}
							renderRecord={(part) => (
								<Select.Option
									disabled={allPartIds.includes(part.id)}
									key={part.id}
									value={part.id}
								>
									<PartWithStockedDropdownDisplay part={part} />
								</Select.Option>
							)}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
						/>
					</Form.Item>
				)}
				<Form.Item
					name="quantity"
					label="Quantity"
					rules={[
						{
							required: true,
							message: 'Please add quantity.',
						},
						{ type: 'integer', min: 1, message: 'Quantity must be a whole number of at least 1' },
					]}
				>
					<InputNumber style={{ width: '100%' }} />
				</Form.Item>
				<Form.Item name="vendorId" label="Vendor">
					<OWAsyncSelect
						disabled={prSelectionExist}
						stateSlice={vendors}
						valueAccessor={(el) => el.id}
						targetCollectionName={PART_VENDORS_TC_NAME}
						createNewEntity={
							canCreateVendors ? (name) => createVendor({ name, currencyId: 'USD' }) : undefined
						}
						fetchData={(searchText, targetCollectionName) =>
							fetchVendors(
								{
									name: searchText,
								},
								targetCollectionName
							)
						}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleVendors(ids, targetCollectionName);
						}}
						placeholder="Select Vendor"
						renderRecord={(vendor) => (
							<Select.Option key={vendor.id} value={vendor.id}>
								{vendor.name}
							</Select.Option>
						)}
					/>
				</Form.Item>
				<PartEquipmentCostFormField
					entityId={partId}
					unitCost={unitCost}
					entityName="partId"
					vendorId={vendorId}
					fetchMethod={fetchPartsCatalog}
					setFieldsValue={setFieldsValue}
					form={form}
					currencyId={currencyId}
					quantity={quantity}
					entityCost={nullSafeGet('cost', partRecord)}
					displayTotalCostOnlyWithValue
				/>
				<ModalFormFooter
					okText={isEditingPart ? 'Save Changes' : 'Add Part'}
					cancelText="Cancel"
					loading={addingPart || loading}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	parts: state.parts,
	currentUser: state.session.currentUser,
	unitsOfMeasure: state.units_of_measure,
	vendors: state.vendors,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	createPart: (entity) => dispatch(partsRestCrudThunksForSupplier.create(entity)),
	updatePart: (entity) => dispatch(partsRestCrudThunksForSupplier.update(entity)),
	fetchPart: (id) => dispatch(partCatalogsRestCrudThunksForSupplier.readOne(id)),
	fetchParts: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			partsRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleParts: (ids, targetCollectionName) =>
		dispatch(partsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchVendors: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...(params || {}), isActive: true, no_pagination: true },
				targetCollectionName
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchPartsCatalog: (params, targetCollectionName) =>
		dispatch(partCatalogsRestCrudThunksForSupplier.read(params, targetCollectionName)),
	createVendor: (entity) => dispatch(vendorsRestCrudThunksForSupplier.create(entity)),
	createPartsPerCatalog: (entity, targetCollectionName) =>
		dispatch(partCatalogsRestCrudThunksForSupplier.create(entity, 'id', targetCollectionName)),
	fetchUnitsOfMeasure: (params) => dispatch(unitsOfMeasureRestCrudThunksForSupplier.read(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPartInPRFrom);
